/**
 * Shared image-insertion service.
 *
 * Inserts raster images into the SVG canvas as <image> elements with an
 * embedded data URI (so the artifact autosave persists them), records a
 * history entry and fires the `changed` event that triggers the artifact
 * autosave in session.
 */

/**
 * Reads a Blob/File as a data URL.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export const blobToDataURL = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve(reader.result)
  reader.onerror = () => reject(reader.error ?? new Error('insertImageService: read error'))
  reader.readAsDataURL(blob)
})

/**
 * Computes an integer scale so the image fits within the canvas resolution
 * (never upscaled beyond 1:1).
 * @param {number} natW
 * @param {number} natH
 * @param {{w: number, h: number}} res canvas resolution
 * @returns {number}
 */
const fitScale = (natW, natH, res) => {
  const k = Math.max(natW / res.w, natH / res.h, 1)
  return 1 / k
}

/**
 * Inserts one raster image (data URL) centered on the current view, adds it
 * to the undo history and emits `changed` (artifact autosave hook).
 * @param {string} dataUrl image data URI
 * @param {string} [name] optional label for the undo entry
 * @returns {Promise<SVGImageElement>} the inserted image element
 */
export const insertImageFromDataUrl = async (dataUrl, name = 'image') => {
  const svgCanvas = window.svgEditor.svgCanvas
  const { naturalWidth: natW, naturalHeight: natH } = await new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({
      naturalWidth: img.naturalWidth || img.width,
      naturalHeight: img.naturalHeight || img.height
    })
    img.onerror = () => reject(new Error('insertImageService: cannot decode image'))
    img.src = dataUrl
  })

  const res = svgCanvas.getResolution()
  const scale = fitScale(natW, natH, res)
  const w = Math.round(natW * scale)
  const h = Math.round(natH * scale)

  const { BatchCommand, InsertElementCommand } = svgCanvas.history
  const newImage = svgCanvas.addSVGElementsFromJson({
    element: 'image',
    attr: {
      x: 0,
      y: 0,
      width: w,
      height: h,
      id: svgCanvas.getNextId(),
      style: 'pointer-events:inherit'
    }
  })
  svgCanvas.setHref(newImage, dataUrl)
  svgCanvas.selectOnly([newImage])
  // Center on the page, same convention as the import menu.
  svgCanvas.alignSelectedElements('m', 'page')
  svgCanvas.alignSelectedElements('c', 'page')

  const batch = new BatchCommand(`Insert ${name}`)
  batch.addSubCommand(new InsertElementCommand(newImage))
  svgCanvas.addCommandToHistory(batch)
  // `changed` drives the TL4K artifact autosave (App.tsx listens for it).
  svgCanvas.call('changed', [newImage])
  window.svgEditor.topPanel.updateContextPanel()
  return newImage
}

/**
 * Inserts several image files (local file picker or drag&drop payload).
 * @param {File[]} files
 * @returns {Promise<SVGImageElement[]>}
 */
export const insertImageFiles = async (files) => {
  const out = []
  for (const file of files) {
    if (!file.type.startsWith('image/')) continue // eslint-disable-line no-continue
    const dataUrl = await blobToDataURL(file)
    out.push(await insertImageFromDataUrl(dataUrl, file.name))
  }
  if (out.length === 0) {
    throw new Error('insertImageService: no image file in selection')
  }
  return out
}

/**
 * Fetches an image URL and embeds it as data URI.
 * Throws on failure so the caller can surface a visible error — artifacts
 * must stay self-contained, a bare external URL would break on offline use.
 * @param {string} url
 * @returns {Promise<{dataUrl: string, embedded: true}>}
 */
export const fetchImageForEmbed = async (url) => {
  const response = await fetch(url, { mode: 'cors', cache: 'force-cache' })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching ${url}`)
  }
  const blob = await response.blob()
  if (!blob.type.startsWith('image/')) {
    throw new Error(`${url} is not an image (${blob.type})`)
  }
  return { dataUrl: await blobToDataURL(blob), embedded: true }
}
